import {
  AccountToOrganizationMembershipStatus,
  ComplianceProgramParticipantStatus,
  GetComplianceProgramProgressQuery,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ManagedTable from "@components/table/managed-table";
import { TableSticky } from "@components/table/table-constants";
import { Tooltip } from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { getUrlUsingTypename } from "@helpers/helpers";

import { useProgramsSection } from "../programs-section-context";
import {
  complianceProgramReportingTableColNameClassName,
  complianceProgramReportingTableColsClassName,
  getColumnDueDate,
} from "./compliance-program-progress-constants";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";
import ProgramProgressColumnHeader from "./program-progress-column-header";
import ProgramProgressPill from "./program-progress-pill";

const ComplianceProgramProgressUserTable = ({
  participantStatuses,
  cols,
  title,
  complianceProgram,
  usersLabel,
}: {
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[];
  cols: ComplianceProgramReportingEntityTableCol[];
  title: string;
  usersLabel: string;
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
}) => {
  const label = useLabel();
  const { noun } = useProgramsSection();

  return (
    <>
      <div className="text-sm font-medium text-gray-700 mt-8 mb-2">{title}</div>
      {participantStatuses.length === 0 ? (
        <div className="text-gray-500 text-sm">
          No {usersLabel} participating to this {noun.toLowerCase()}.
        </div>
      ) : (
        <ManagedTable
          scroll
          sortingEnabled
          sortDir="asc"
          sortKey="name"
          data={participantStatuses}
          className="overscroll-x-contain max-h-[75dvh]"
          aria-label={`${noun} progress user table`}
          sticky={TableSticky.onlyTop}
          columns={[
            {
              headerClassName: complianceProgramReportingTableColNameClassName,
              cellClassName: complianceProgramReportingTableColNameClassName,
              header: `Name`,
              render: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
              ) => {
                const isEngagementSurvey =
                  !!complianceProgram.engagementSurveyTemplate;
                const assessmentDelivery = !isEngagementSurvey
                  ? complianceProgram.assessmentDeliveries.edges.find(
                      (edge) => edge?.node?.target?.id === data.user.id,
                    )
                  : complianceProgram.assessmentDeliveries.edges[0];
                return (
                  <div className="min-w-0 flex flex-nowrap grow shrink-0 items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                      <div
                        title={data.user.name}
                        className={classNames(
                          "text-ellipsis overflow-hidden text-nowrap",
                          data.user.membershipStatus ===
                            AccountToOrganizationMembershipStatus.Inactive &&
                            "text-gray-300",
                        )}
                      >
                        {data.user.name}
                      </div>
                      {data.user.membershipStatus ===
                        AccountToOrganizationMembershipStatus.Inactive && (
                        <Tooltip text="Deactivated">
                          <span>
                            <TopicflowIcon
                              type="deactivated"
                              className="text-gray-300 h-5 w-5"
                            />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    {/* The link to the package, on the rows that have one. Its slot is
                        always held so the names stay in one line down the column, but a
                        greyed-out icon on every row of a review nobody has finished yet
                        is noise: an absent link says "no package" just as clearly. */}
                    {!isEngagementSurvey && (
                      <div className="w-[32px] shrink-0">
                        {assessmentDelivery?.node && (
                          <Button
                            theme={buttonTheme.iconGray}
                            icon={TopicflowIconMap.externalLink}
                            to={getUrlUsingTypename(assessmentDelivery.node)}
                            tooltip={`Go to ${label("review", {
                              capitalize: true,
                            })} Package`}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              },
              sortKey: "name",
              value: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
              ) => {
                return data.user.name;
              },
            },
            ...cols.map((col) => ({
              headerClassName: complianceProgramReportingTableColsClassName,
              cellClassName: complianceProgramReportingTableColsClassName,
              header: col.label,
              headerRender: () => (
                <ProgramProgressColumnHeader
                  label={col.label}
                  due={getColumnDueDate(participantStatuses, col.keyName)}
                />
              ),
              sortKey: col.keyName,
              value: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
              ) =>
                data[col.keyName] ===
                ComplianceProgramParticipantStatus.Complete
                  ? 4
                  : data[col.keyName] ===
                      ComplianceProgramParticipantStatus.InProgress
                    ? 3
                    : data[col.keyName] ===
                        ComplianceProgramParticipantStatus.NotStarted
                      ? 2
                      : 1,
              render: (
                data: ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
              ) => {
                return (
                  <ProgramProgressPill
                    col={col}
                    participantStatus={data}
                    status={data[col.keyName]}
                    complianceProgram={complianceProgram}
                    className="hover:brightness-95"
                  />
                );
              },
            })),
            {
              header: ``,
              render: () => "",
            },
          ]}
        />
      )}
    </>
  );
};

export default ComplianceProgramProgressUserTable;
